<!-- Новая справка по титулам; её навигация не меняет выбор на основной странице. -->
<template>
  <BaseModal
    :visible="visible && Boolean(currentItem)"
    size="lg"
    footer-align="between"
    adaptive-fullscreen
    :aria-label="modalAriaLabel"
    @close="$emit('close')"
  >
    <template #header>
      <div v-if="currentItem" class="edo-title-help__header">
        <span class="edo-title-help__header-icon" aria-hidden="true">
          <AppIcon name="documentOutline" :size="22" />
        </span>
        <div class="edo-title-help__header-copy">
          <span>Что это за титул</span>
          <h3>
            {{ currentItem.label }}<template v-if="currentItem.role"> · {{ currentItem.role }}</template>
          </h3>
        </div>
        <BaseBadge
          v-if="currentItem.statusLabel"
          class="edo-title-help__desktop-status"
          :label="currentItem.statusLabel"
          :variant="currentItem.statusVariant || 'default'"
        />
      </div>
    </template>

    <div v-if="currentItem" class="edo-title-help__content">
      <BaseBadge
        v-if="currentItem.statusLabel"
        class="edo-title-help__mobile-status"
        :label="currentItem.statusLabel"
        :variant="currentItem.statusVariant || 'default'"
      />
      <p v-if="currentItem.summary" class="edo-title-help__summary">
        {{ currentItem.summary }}
      </p>

      <div class="edo-title-help__facts">
        <div
          v-for="fact in facts"
          :key="fact.key"
          class="edo-title-help__fact"
        >
          <EdoSectionIcon :name="fact.icon" />
          <span class="edo-title-help__fact-label">{{ fact.label }}</span>
          <p>{{ fact.value }}</p>
        </div>
      </div>

      <BaseInlineStatus
        v-if="currentItem.note"
        kind="warning"
        :text="currentItem.note"
      />
    </div>

    <template #footer>
      <div class="edo-title-help__footer">
        <base-button
          variant="secondary"
          icon="chevronLeft"
          type="button"
          :disabled="!previousItem"
          @click="selectPrevious"
        >
          {{ previousItem ? previousItem.label : "Начало" }}
        </base-button>
        <span class="edo-title-help__position">
          Титул {{ currentIndex + 1 }} из {{ items.length }}
        </span>
        <base-button
          variant="secondary"
          right-icon="chevronRight"
          type="button"
          :disabled="!nextItem"
          @click="selectNext"
        >
          {{ nextItem ? nextItem.label : "Конец" }}
        </base-button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import EdoSectionIcon from "@/components/Edo/shared/icons/EdoSectionIcon.vue";

export default {
  name: "EdoDocumentTitleHelpModal",

  components: {
    AppIcon,
    EdoSectionIcon,
  },

  emits: ["close"],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    initialKey: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      selectedKey: null,
    };
  },

  computed: {
    currentIndex() {
      return this.items.findIndex((item) => item.key === this.selectedKey);
    },
    currentItem() {
      return this.items[this.currentIndex] || null;
    },
    previousItem() {
      return this.currentIndex > 0 ? this.items[this.currentIndex - 1] : null;
    },
    nextItem() {
      return this.items[this.currentIndex + 1] || null;
    },
    modalAriaLabel() {
      if (!this.currentItem) return "Справка по титулу ЭДО";

      return `Что это за титул: ${this.currentItem.label} ${this.currentItem.role || ""}`.trim();
    },
    facts() {
      if (!this.currentItem) return [];

      return [
        {
          key: "payload",
          icon: "cargo",
          label: "Что фиксируется",
          value: this.currentItem.payload,
        },
        {
          key: "next",
          icon: "route",
          label: "Что дальше",
          value: this.currentItem.next,
        },
        {
          key: "participant",
          icon: "participants",
          label: "Участник этапа",
          value: this.currentItem.participant,
        },
      ].filter((fact) => fact.value);
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) this.selectInitialItem();
      },
    },
    initialKey() {
      if (this.visible) this.selectInitialItem();
    },
    items() {
      if (this.visible) this.selectInitialItem();
    },
  },

  methods: {
    // Выбор внутри справки намеренно локальный: он не меняет титул на странице.
    selectInitialItem() {
      const initialItem = this.items.find((item) => item.key === this.initialKey);
      this.selectedKey = initialItem?.key || this.items[0]?.key || null;
    },
    selectPrevious() {
      if (this.previousItem) this.selectedKey = this.previousItem.key;
    },
    selectNext() {
      if (this.nextItem) this.selectedKey = this.nextItem.key;
    },
  },
};
</script>

<style scoped>
.edo-title-help__header {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.edo-title-help__header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #eef2ff;
  color: #2f4ac0;
}

.edo-title-help__header-copy {
  display: grid;
  flex: 1;
  gap: 3px;
  min-width: 0;
}

.edo-title-help__header-copy > span,
.edo-title-help__fact-label,
.edo-title-help__position {
  color: #5b6070;
  font-size: 13px;
}

.edo-title-help__header-copy h3 {
  margin: 0;
  color: var(--color-dark);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.edo-title-help__content {
  display: grid;
  gap: 16px;
}

.edo-title-help__mobile-status {
  display: none;
  justify-self: start;
}

.edo-title-help__summary,
.edo-title-help__fact p {
  margin: 0;
  color: var(--color-dark);
  line-height: 1.5;
}

.edo-title-help__summary {
  font-size: 15px;
}

.edo-title-help__facts {
  display: grid;
}

.edo-title-help__fact {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  gap: 4px 10px;
  padding: 12px 0;
}

.edo-title-help__fact + .edo-title-help__fact {
  border-top: 1px solid #eef0f5;
}

.edo-title-help__fact > svg {
  grid-row: 1 / 3;
  margin-top: 1px;
  color: #5b6070;
}

.edo-title-help__fact p {
  grid-column: 2;
  font-size: 14px;
}

.edo-title-help__footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  width: 100%;
}

.edo-title-help__footer > :first-child {
  justify-self: start;
}

.edo-title-help__footer > :last-child {
  justify-self: end;
}

.edo-title-help__position {
  text-align: center;
}

@media (max-width: 720px) {
  .edo-title-help__desktop-status {
    display: none;
  }

  .edo-title-help__mobile-status {
    display: inline-block;
  }

  .edo-title-help__content > :deep(.base-inline-status) {
    justify-content: flex-start;
  }

  .edo-title-help__footer {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .edo-title-help__position {
    grid-column: 1 / -1;
    grid-row: 1;
  }

  .edo-title-help__footer > :first-child,
  .edo-title-help__footer > :last-child {
    grid-row: 2;
    width: 100%;
    min-height: 40px;
    height: 40px;
  }
}
</style>
